import { useEffect, useMemo, useState } from 'react';
import { useOrgFilter, pickDefaultOrg } from '@/store/defaults';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  refs,
  type PayrollAccountRule,
  type PayrollSubconto,
  type PayrollSubcontoType,
} from '@/api/refs';
import { accounts } from '@/api/documents';
import { employees as employeesApi } from '@/api/hr';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import AccountSelect from '@/components/AccountSelect';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Довідник «Рахунки обліку оплат» — повна модель «як у 1С»
 * (Catalogs.СпособыОтраженияЗарплатыВРеглУчете): кожен ВИД НАРАХУВАННЯ має
 * СТАНДАРТНИЙ рахунок + (опц.) субконто Дт/Кт (до 3) + НУ-призначення.
 *
 * Редагування — через бокову панель (drawer): рахунок виду + субконто + НУ.
 * Перевизначення зберігається як org-wide правило матриці
 * ref.payroll_account_rules (одна колонка + вимір + субконто-рядки), яке
 * прод-резолвер бере поверх стандарту (rule > картка > стандарт). Працює і у
 * проводках, і у прев'ю (Закон #6).
 *
 * Субконто=«<з документа>» (subconto_id=null) зберігає поточну авто-підстановку
 * (працівник/стаття затрат), як у 1С коли субконто типу взято з документа.
 * Персональні рахунки конкретного працівника задаються на КАРТЦI працівника.
 */
const PERMISSION = 'refs.payroll_account_rules.manage';

// Колонка рахунку матриці.
type Col = 'expense_account_id' | 'payroll_account_id' | 'pdfo_account_id'
  | 'wz_account_id' | 'esv_account_id';

const SUBCONTO_TYPE_LABELS: Record<PayrollSubcontoType, string> = {
  'ref.employees': 'Працівник',
  'ref.cost_items': 'Стаття витрат',
  'ref.projects': 'Проєкт',
  'ref.construction_objects': 'Об’єкт будівництва',
};
const SUBCONTO_TYPES = Object.keys(SUBCONTO_TYPE_LABELS) as PayrollSubcontoType[];
const FROM_DOC = '__from_document__'; // маркер «<з документа>» у select значення

/**
 * Види нарахувань. Кожен = (стандартний код, колонка матриці, вимір правила).
 */
interface Vid {
  key: string;
  label: string;
  standard: string;
  column: Col;
  payment_kind: string | null;
  employment_type: string | null;
}
const VIDY: Vid[] = [
  { key: 'expense', label: 'Витрати (Дт)', standard: '92', column: 'expense_account_id', payment_kind: null, employment_type: null },
  { key: 'payroll', label: 'Розрахунки з оплати', standard: '661', column: 'payroll_account_id', payment_kind: null, employment_type: null },
  { key: 'pdfo', label: 'ПДФО', standard: '6411', column: 'pdfo_account_id', payment_kind: null, employment_type: null },
  { key: 'wz', label: 'Військовий збір', standard: '642', column: 'wz_account_id', payment_kind: null, employment_type: null },
  { key: 'esv', label: 'ЄСВ', standard: '651', column: 'esv_account_id', payment_kind: null, employment_type: null },
  { key: 'sick_expense', label: 'Витрати лікарняних', standard: '949', column: 'expense_account_id', payment_kind: 'SICK', employment_type: null },
  { key: 'sick_payroll', label: 'Розрахунки лікарняних', standard: '663', column: 'payroll_account_id', payment_kind: 'SICK', employment_type: null },
  { key: 'vacation_expense', label: 'Витрати відпускних', standard: '92', column: 'expense_account_id', payment_kind: 'VACATION', employment_type: null },
  { key: 'vacation_payroll', label: 'Розрахунки відпускних', standard: '661', column: 'payroll_account_id', payment_kind: 'VACATION', employment_type: null },
  { key: 'compensation_expense', label: 'Витрати компенсації відпустки', standard: '92', column: 'expense_account_id', payment_kind: 'COMPENSATION', employment_type: null },
  { key: 'compensation_payroll', label: 'Розрахунки компенсації відпустки', standard: '661', column: 'payroll_account_id', payment_kind: 'COMPENSATION', employment_type: null },
  { key: 'civil_expense', label: 'Витрати за ЦПУ', standard: '92', column: 'expense_account_id', payment_kind: null, employment_type: 'CIVIL_LAW' },
  { key: 'civil_payroll', label: 'Розрахунки за ЦПУ', standard: '6851', column: 'payroll_account_id', payment_kind: null, employment_type: 'CIVIL_LAW' },
];

interface VidRow extends Vid {
  rowId: string;
  rule: PayrollAccountRule | null;   // org-wide правило, що перевизначає цей вид (якщо є)
  effectiveCode: string;             // що реально спрацює (override або стандарт)
  isStandard: boolean;
  imported: boolean;
  subcontoCount: number;
  hasNu: boolean;
}

// Рядок персонального правила (employee_id) — нестандартний рахунок з ПIБ.
interface PersonalRow {
  rowId: string;
  pib: string;
  vidLabel: string;
  code: string;
  imported: boolean;
  usable: boolean;   // false → рахунок непридатний (забаланс/група) → у проводці НЕ застосується
}

// ---- draft субконто у формі drawer (значення = FROM_DOC або id об'єкта) ----
interface SubDraft {
  account_column: Col;
  slot: number;
  subconto_type: PayrollSubcontoType;
  subconto_id: string | null;
}

export default function PayrollAccountRulesPage() {
  const qc = useQueryClient();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  // Орг за замовчуванням з шапки (override при ручному виборі).
  const [orgId, setOrgId] = useOrgFilter();
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['payroll-account-rules', orgId],
    queryFn: () => refs.payrollAccountRules.list(orgId),
    enabled: !!orgId,
  });
  const accountsQ = useQuery({
    queryKey: ['accounts', 'all'],
    queryFn: () => accounts.list({ only_postable: true, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });
  const nuQ = useQuery({
    queryKey: ['tax-purposes-assets-costs'],
    queryFn: () => refs.taxPurposesAssetsCosts(),
    staleTime: 60 * 60 * 1000,
  });
  // ПIБ працівників — для рядків персональних правил (employee_id).
  const empQ = useQuery({
    queryKey: ['employees-pib', orgId],
    queryFn: () => employeesApi.list({ organization_id: orgId, limit: 1000 }),
    enabled: !!orgId,
  });

  const accountCode = useMemo(() => {
    const m = new Map<string, string>();
    accountsQ.data?.forEach((a) => m.set(a.id, a.code));
    return m;
  }, [accountsQ.data]);
  // Чи придатний рахунок для проводки (дзеркало graceful-фільтра резолвера:
  // не група, не забалансовий). Непридатний → резолвер його ІГНОРУЄ → у проводці
  // спрацює стандарт. На сторінці помічаємо «⚠ не застосовується».
  const accountUsable = useMemo(() => {
    const m = new Map<string, boolean>();
    accountsQ.data?.forEach((a) => m.set(a.id, !a.is_group_only && !a.is_off_balance));
    return m;
  }, [accountsQ.data]);
  const empName = useMemo(() => {
    const m = new Map<string, string>();
    empQ.data?.items.forEach((e) => m.set(e.id, e.full_name ?? e.code ?? e.id));
    return m;
  }, [empQ.data]);

  const canManage = !!orgId && allowedOrgIds.has(orgId);

  // Для кожного виду шукаємо org-wide правило-override.
  const findRule = (v: Vid): PayrollAccountRule | null => {
    for (const r of list.data ?? []) {
      if (r.deletion_mark) continue;
      if (r.employee_id || r.department_id) continue;          // org-wide вид, не персональне
      if ((r.payment_kind ?? null) !== v.payment_kind) continue;
      if ((r.employment_type ?? null) !== v.employment_type) continue;
      if (!r[v.column]) continue;                              // правило має задавати саме цю колонку
      return r;
    }
    return null;
  };

  const rows = useMemo<VidRow[]>(() => VIDY.map((v) => {
    const rule = findRule(v);
    const overrideId = rule ? rule[v.column] : null;
    const effectiveCode = overrideId ? (accountCode.get(overrideId) ?? '…') : v.standard;
    // субконто саме цієї колонки (інші колонки правила тут не показуємо).
    const subForCol = (rule?.subconto ?? []).filter((s) => s.account_column === v.column);
    return {
      ...v, rowId: v.key, rule,
      effectiveCode,
      isStandard: !rule,
      imported: !!rule?.external_source,
      subcontoCount: subForCol.length,
      hasNu: !!rule?.tax_purpose_assets_costs_id,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [list.data, accountCode]);

  // Персональні правила (employee_id заданий) — окрема таблиця з ПIБ. Кожен
  // заданий рахунок правила → окремий рядок (вид нарахування + рахунок + ПIБ).
  const PERSONAL_COLS: { col: Col; label: string }[] = [
    { col: 'expense_account_id', label: 'Витрати' },
    { col: 'payroll_account_id', label: 'Розрахунки' },
    { col: 'pdfo_account_id', label: 'ПДФО' },
    { col: 'wz_account_id', label: 'Військовий збір' },
    { col: 'esv_account_id', label: 'ЄСВ' },
  ];
  const personalRows = useMemo<PersonalRow[]>(() => {
    const out: PersonalRow[] = [];
    for (const r of list.data ?? []) {
      if (r.deletion_mark || !r.employee_id) continue;       // лише персональні
      for (const { col, label } of PERSONAL_COLS) {
        const accId = r[col];
        if (!accId) continue;
        out.push({
          rowId: `${r.id}:${col}`,
          pib: empName.get(r.employee_id) ?? r.employee_id,
          vidLabel: label,
          code: accountCode.get(accId) ?? '…',
          imported: !!r.external_source,
          usable: accountUsable.get(accId) ?? true,  // невідомий → не лякаємо
        });
      }
    }
    return out.sort((a, b) => a.pib.localeCompare(b.pib) || a.vidLabel.localeCompare(b.vidLabel));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, accountCode, accountUsable, empName]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['payroll-account-rules', orgId] });

  // ---- Drawer state ----
  const [editVid, setEditVid] = useState<Vid | null>(null);
  const [drAccountId, setDrAccountId] = useState<string>('');
  const [drNu, setDrNu] = useState<string>('');
  const [drSub, setDrSub] = useState<SubDraft[]>([]);

  const openDrawer = (r: VidRow) => {
    const rule = r.rule;
    setEditVid(r);
    // рахунок: override або стандарт-код → шукаємо id стандарту через accountsQ.
    setDrAccountId(rule ? (rule[r.column] ?? '') : '');
    setDrNu(rule?.tax_purpose_assets_costs_id ?? '');
    setDrSub(
      (rule?.subconto ?? [])
        .filter((s) => s.account_column === r.column)
        .map((s) => ({
          account_column: s.account_column as Col,
          slot: s.slot,
          subconto_type: s.subconto_type,
          subconto_id: s.subconto_id,
        })),
    );
  };
  const closeDrawer = () => { setEditVid(null); setDrSub([]); setDrNu(''); setDrAccountId(''); };

  const addSubRow = () => {
    if (!editVid) return;
    const used = new Set(drSub.map((s) => s.slot));
    const slot = [1, 2, 3].find((n) => !used.has(n));
    if (slot === undefined) return; // вже 3 субконто на колонку
    setDrSub([...drSub, {
      account_column: editVid.column, slot,
      subconto_type: 'ref.employees', subconto_id: null,
    }]);
  };
  const removeSubRow = (idx: number) => setDrSub(drSub.filter((_, i) => i !== idx));
  const patchSubRow = (idx: number, patch: Partial<SubDraft>) =>
    setDrSub(drSub.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  // Збереження drawer: upsert правило + субконто + НУ АБО видалення (повний стандарт).
  const save = useMutation({
    mutationFn: async () => {
      if (!editVid) return;
      const v = editVid;
      const rule = findRule(v);
      const newCode = drAccountId ? accountCode.get(drAccountId) : '';
      const backToStandard = !drAccountId || newCode === v.standard;
      const subconto: PayrollSubconto[] = drSub.map((s) => ({
        account_column: s.account_column,
        slot: s.slot,
        subconto_type: s.subconto_type,
        subconto_id: s.subconto_id,
      }));
      const hasExtras = subconto.length > 0 || !!drNu;

      // Повний стандарт (рахунок=стандарт І немає субконто/НУ) → прибрати правило.
      if (backToStandard && !hasExtras) {
        if (rule) await refs.payrollAccountRules.remove(rule.id);
        return;
      }
      // Колонка-рахунок: override-id, або (якщо стандарт але є субконто/НУ) — id стандарту.
      const colAccountId = backToStandard
        ? (accountsQ.data?.find((a) => a.code === v.standard)?.id ?? null)
        : drAccountId;

      if (rule) {
        await refs.payrollAccountRules.update(rule.id, {
          [v.column]: colAccountId,
          tax_purpose_assets_costs_id: drNu || null,
          subconto,
        });
      } else {
        await refs.payrollAccountRules.create({
          organization_id: orgId,
          payment_kind: v.payment_kind,
          employment_type: v.employment_type,
          [v.column]: colAccountId,
          tax_purpose_assets_costs_id: drNu || null,
          subconto,
          priority: 5, is_active: true,
          comment: `Спосіб відображення «${v.label}»`,
        });
      }
    },
    onSuccess: () => { invalidate(); closeDrawer(); },
  });

  const cols = useMemo<ColumnDef<VidRow>[]>(() => [
    {
      key: 'vid', header: 'Вид нарахування', width: 220,
      sortValue: (r) => r.label, cell: (r) => r.label,
    },
    {
      key: 'account', header: 'Рахунок', width: 110,
      cell: (r) => <b>{r.effectiveCode}</b>,
    },
    {
      key: 'subconto', header: 'Субконто / НУ', width: 150,
      cell: (r) => (
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
          {r.subcontoCount > 0 ? `${r.subcontoCount} субконто` : '—'}
          {r.hasNu && <span title="Задано НУ-призначення"> · НУ</span>}
        </span>
      ),
    },
    {
      key: 'type', header: 'Тип', width: 130,
      sortValue: (r) => (r.isStandard ? 0 : 1),
      cell: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {r.imported && (
            <span title="Імпортовано з 1С" style={{
              fontSize: '0.7rem', fontWeight: 700, color: '#2563eb',
              border: '1px solid var(--badge-blue-bd)', borderRadius: 4, padding: '0 4px',
            }}>I</span>
          )}
          <span style={{ color: r.isStandard ? '#16a34a' : '#b45309', fontWeight: 600 }}>
            {r.isStandard ? 'стандарт' : 'нестандарт'}
          </span>
        </span>
      ),
    },
    {
      key: 'edit', header: '', width: 110,
      cell: (r) => (
        canManage
          ? <button className="btn-sm" onClick={() => openDrawer(r)}>⚙ Налаштувати</button>
          : null
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canManage, accountCode]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Рахунки обліку оплат</h2>
      </div>

      {/* Фільтр юрособи — окремим рядком ліворуч над таблицею. */}
      <div style={{ display: 'flex', marginBottom: '0.6rem' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
                style={{ minWidth: 220 }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
      </div>

      <DataTable<VidRow>
        rows={rows}
        columns={cols}
        rowKey={(r) => r.rowId}
        loading={list.isLoading || accountsQ.isLoading}
        emptyText="—"
      />

      {personalRows.length > 0 && (
        <div style={{ marginTop: '1.2rem' }}>
          <h3 style={{ margin: '0 0 0.3rem', fontSize: '1rem' }}>
            Персональні рахунки працівників
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: '0 0 0.4rem' }}>
            Нестандартні рахунки конкретних працівників (з картки або імпорту з 1С).
            Вони перебивають загальні рахунки вище. Редагуються на картці працівника.
          </p>
          <table className="data-table" style={{ width: '100%', fontSize: '0.88rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Працівник</th>
                <th style={{ textAlign: 'left' }}>Вид нарахування</th>
                <th style={{ textAlign: 'left' }}>Рахунок</th>
                <th style={{ textAlign: 'left' }}>Тип</th>
              </tr>
            </thead>
            <tbody>
              {personalRows.map((p) => (
                <tr key={p.rowId}>
                  <td>{p.pib}</td>
                  <td>{p.vidLabel}</td>
                  <td>
                    <b style={p.usable ? undefined : { color: '#dc2626', textDecoration: 'line-through' }}>
                      {p.code}
                    </b>
                    {!p.usable && (
                      <span
                        title="Рахунок непридатний для проводки (забалансовий / група / видалений). У реальній проводці він НЕ застосується — спрацює стандартний рахунок. Виправте на картці працівника."
                        style={{ color: '#dc2626', fontSize: '0.78rem', marginLeft: 6 }}
                      >
                        ⚠ не застосовується
                      </span>
                    )}
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {p.imported && (
                        <span title="Імпортовано з 1С" style={{
                          fontSize: '0.7rem', fontWeight: 700, color: '#2563eb',
                          border: '1px solid var(--badge-blue-bd)', borderRadius: 4, padding: '0 4px',
                        }}>I</span>
                      )}
                      <span style={{ color: 'var(--badge-amber-fg)', fontWeight: 600 }}>нестандарт</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editVid && (
        <Drawer
          vid={editVid}
          accountId={drAccountId}
          setAccountId={setDrAccountId}
          nu={drNu}
          setNu={setDrNu}
          nuOptions={nuQ.data ?? []}
          sub={drSub}
          addSubRow={addSubRow}
          removeSubRow={removeSubRow}
          patchSubRow={patchSubRow}
          onClose={closeDrawer}
          onSave={() => save.mutate()}
          saving={save.isPending}
        />
      )}
    </div>
  );
}

// ============================================================ Drawer

interface DrawerProps {
  vid: Vid;
  accountId: string;
  setAccountId: (v: string) => void;
  nu: string;
  setNu: (v: string) => void;
  nuOptions: { id: string; code: string; name_uk: string }[];
  sub: SubDraft[];
  addSubRow: () => void;
  removeSubRow: (idx: number) => void;
  patchSubRow: (idx: number, patch: Partial<SubDraft>) => void;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
}

function Drawer(p: DrawerProps) {
  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 460, zIndex: 50,
      background: 'var(--bg-panel)', boxShadow: '-4px 0 16px rgba(0,0,0,0.15)',
      padding: '1rem', overflowY: 'auto', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Спосіб: {p.vid.label}</h3>
        <button className="btn-sm" onClick={p.onClose}>✕</button>
      </div>

      <label style={{ marginTop: '0.8rem', fontWeight: 600 }}>Рахунок</label>
      <AccountSelect
        value={p.accountId}
        onChange={p.setAccountId}
        placeholder={`— стандарт ${p.vid.standard} —`}
      />

      <label style={{ marginTop: '0.8rem', fontWeight: 600 }}>Податкове призначення (НУ)</label>
      <select value={p.nu} onChange={(e) => p.setNu(e.target.value)}>
        <option value="">— не задано —</option>
        {p.nuOptions.map((o) => (
          <option key={o.id} value={o.id}>{o.code} — {o.name_uk}</option>
        ))}
      </select>

      <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center' }}>
        <span style={{ fontWeight: 600 }}>Субконто ({p.sub.length}/3)</span>
        <button className="btn-sm" onClick={p.addSubRow} disabled={p.sub.length >= 3}>
          + Додати
        </button>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', margin: '0.2rem 0' }}>
        «&lt;з документа&gt;» — значення береться автоматично з документа ЗП
        (як зараз: працівник / стаття витрат). Конкретний об'єкт — фіксоване субконто.
      </p>
      {p.sub.map((s, idx) => (
        <SubcontoRow key={idx} draft={s}
          onType={(t) => p.patchSubRow(idx, { subconto_type: t, subconto_id: null })}
          onValue={(id) => p.patchSubRow(idx, { subconto_id: id })}
          onRemove={() => p.removeSubRow(idx)}
        />
      ))}

      <div style={{ marginTop: 'auto', paddingTop: '1rem', display: 'flex', gap: '0.5rem' }}>
        <button className="btn" onClick={p.onSave} disabled={p.saving}>
          {p.saving ? 'Збереження…' : 'Зберегти'}
        </button>
        <button className="btn btn-secondary" onClick={p.onClose}>Скасувати</button>
      </div>
    </div>
  );
}

interface SubRowProps {
  draft: SubDraft;
  onType: (t: PayrollSubcontoType) => void;
  onValue: (id: string | null) => void;
  onRemove: () => void;
}

function SubcontoRow({ draft, onType, onValue, onRemove }: SubRowProps) {
  // Список об'єктів обраного типу (для конкретного значення). employees/cost_items/
  // projects мають готовий API; construction_objects — поки лише «<з документа>».
  const objs = useQuery({
    queryKey: ['subconto-objs', draft.subconto_type],
    queryFn: async () => {
      if (draft.subconto_type === 'ref.cost_items') {
        const r = await refs.costItems();
        return r.map((x: { id: string; name: string }) => ({ id: x.id, label: x.name }));
      }
      return [] as { id: string; label: string }[];
    },
    staleTime: 60 * 60 * 1000,
    // employees/projects потребують organization_id — для MVP конкретний об'єкт
    // підтримано для cost_items; інші типи — «<з документа>» (головний кейс 1С).
    enabled: draft.subconto_type === 'ref.cost_items',
  });

  const canPickObject = draft.subconto_type === 'ref.cost_items';

  return (
    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.4rem' }}>
      <select value={draft.subconto_type}
              onChange={(e) => onType(e.target.value as PayrollSubcontoType)}
              style={{ flex: '1 1 40%' }}>
        {SUBCONTO_TYPES.map((t) => (
          <option key={t} value={t}>{SUBCONTO_TYPE_LABELS[t]}</option>
        ))}
      </select>
      <select
        value={draft.subconto_id ?? FROM_DOC}
        onChange={(e) => onValue(e.target.value === FROM_DOC ? null : e.target.value)}
        style={{ flex: '1 1 50%' }}
        disabled={!canPickObject}
        title={canPickObject ? undefined : 'Для цього типу — лише «з документа»'}
      >
        <option value={FROM_DOC}>&lt;з документа&gt;</option>
        {objs.data?.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
      <button className="btn-sm" onClick={onRemove} title="Видалити">✕</button>
    </div>
  );
}
